import { isHexColor } from './contrast'
import { type ThemeMode, buildSemantic } from './semantic'

const toRgb = (hex: string) => {
  const n = Number.parseInt(hex.slice(1, 7), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255] as const
}

/** Hoà `top` lên `base` theo tỉ lệ `t` (0–1), trả `#RRGGBB`. */
export const mixHex = (base: string, top: string, t: number): string => {
  const a = toRgb(base)
  const b = toRgb(top)
  const out = a.map((v, i) => Math.round(v * (1 - t) + b[i] * t))
  return `#${out.map((v) => v.toString(16).padStart(2, '0')).join('')}`.toUpperCase()
}

/** Tỉ lệ pha màu chủ đạo vào từng lớp nền. Nhỏ để chữ giữ tương phản AA/AAA. */
const TINT: Record<ThemeMode, { page: number; surface: number; sunken: number; elevated: number }> =
  {
    light: { page: 0.1, surface: 0.03, sunken: 0.07, elevated: 0.03 },
    dark: { page: 0.1, surface: 0.09, sunken: 0.12, elevated: 0.12 },
  }

export type SurfaceTint = { page: string; surface: string; sunken: string; elevated: string }

/**
 * Sinh bộ nền (trang, sider/header/thẻ, vùng lõm, popover) ngả theo màu chủ đạo.
 * Nền gốc lấy từ semantic nên sáng/tối đều đúng; chữ neutral không đổi.
 */
export const deriveSurfaceTint = (primary: string, mode: ThemeMode): SurfaceTint | null => {
  if (!isHexColor(primary)) return null
  const bg = buildSemantic(mode, 'brand').bg
  const t = TINT[mode]
  return {
    page: mixHex(bg.page, primary, t.page),
    surface: mixHex(bg.surface, primary, t.surface),
    sunken: mixHex(bg.sunken, primary, t.sunken),
    elevated: mixHex(bg.elevated, primary, t.elevated),
  }
}

/** Biến CSS ghi đè nền khi có màu chủ đạo tuỳ chọn (hover = lớp mờ của chính màu chủ đạo). */
export const buildTintCssVars = (
  primary: string,
  mode: ThemeMode,
  surfaces?: Pick<SurfaceTint, 'page' | 'surface' | 'sunken'>
): Record<string, string> => {
  const derived = deriveSurfaceTint(primary, mode)
  if (!derived) return {}
  const tint = surfaces ? { ...derived, ...surfaces, elevated: surfaces.surface } : derived
  return {
    '--vbdt-color-bg-page': tint.page,
    '--vbdt-color-bg-surface': tint.surface,
    '--vbdt-color-bg-sunken': tint.sunken,
    '--vbdt-color-bg-elevated': tint.elevated,
    '--vbdt-color-bg-hover': `color-mix(in srgb, ${primary} ${mode === 'dark' ? 16 : 8}%, transparent)`,
  }
}
